import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Lock, Loader2, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { toast } from 'sonner';

const BENEFITS = [
  {
    emoji: '\u{1F436}', // 🐶
    title: 'Save your dog\u2019s profile',
    desc: 'Ted remembers your dog\u2019s breed, age, allergies and favourite foods for personalised recommendations.',
  },
  {
    emoji: '\u{1F916}', // 🤖
    title: 'AI food scans',
    desc: 'Every bag you scan is saved, so you can compare foods and revisit Ted\u2019s advice anytime.',
  },
  {
    emoji: '\u{1F6D2}', // 🛒
    title: 'Fast reordering',
    desc: 'Reorder your favourite foods, treats and sample boxes in just a few taps.',
  },
  {
    emoji: '\u{1F9B4}', // 🦴
    title: 'TED\u2019S TABLE',
    desc: 'Ted remembers your dog\u2019s favourite treats, making table ordering quick and easy.',
  },
  {
    emoji: '\u{1F4B0}', // 💰
    title: 'Price drop alerts',
    desc: 'Ted lets you know when your favourite food or treats go on sale.',
  },
  {
    emoji: '\u{1F381}', // 🎁
    title: 'Exclusive member rewards',
    desc: 'Access special offers, new product launches and member-only discounts.',
  },
];

function formatError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export const SignUpPage = ({ onSuccess }) => {
  useDocumentTitle(
    'Create Account — Ultimate Pet Foods',
    'Sign up to save your dog\u2019s profile, track orders, and get notified when your favourite dog food goes on sale.'
  );
  const { register, customer } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // If already logged in, skip straight to account
  useEffect(() => {
    if (customer) {
      onSuccess?.();
    }
  }, [customer, onSuccess]);

  const passwordStrength = (() => {
    if (!password) return null;
    const long = password.length >= 8;
    const varied = /[A-Za-z]/.test(password) && /[0-9]/.test(password);
    if (long && varied) return { label: 'Strong', color: 'text-emerald-600', bar: 'bg-emerald-500 w-full' };
    if (long || varied) return { label: 'OK', color: 'text-amber-600', bar: 'bg-amber-500 w-2/3' };
    return { label: 'Weak', color: 'text-red-500', bar: 'bg-red-500 w-1/3' };
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) { setError('Please enter your name'); return; }
    if (!email.trim()) { setError('Please enter your email'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }

    setSubmitting(true);
    try {
      // If the customer landed on /signup via the Ted's Table QR banner
      // (?from=teds-table), tag their account with that source so the shop
      // can segment/target them later.
      const source = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('from') === 'teds-table'
        ? 'teds_table'
        : null;
      await register(name.trim(), email.trim(), password, source);
      toast.success(`Welcome, ${name.trim().split(' ')[0]}! Your account is ready.`);
      onSuccess?.();
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gradient-to-b from-[#f0fdfa] via-white to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left: benefits */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="order-2 lg:order-1"
          >
            <p className="text-xs uppercase tracking-[3px] text-[#0FA49C] font-bold mb-3">Free — Takes 30 Seconds</p>
            <h1 className="text-4xl sm:text-5xl font-black text-[#1a1a1a] leading-tight mb-4">
              Your dog&apos;s personal<br/>
              <span className="text-[#0FA49C]">AI assistant</span>
            </h1>
            <p className="text-base text-gray-600 mb-8 max-w-lg">
              One free account gives Ted everything he needs to recommend the best food, remember your dog&apos;s favourites, unlock exclusive offers and make ordering faster.
            </p>

            <div className="space-y-4">
              {BENEFITS.map((b, i) => (
                <motion.div
                  key={b.title}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 + i * 0.06 }}
                  className="flex items-start gap-3"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#0FA49C]/10 flex items-center justify-center flex-shrink-0 text-xl">
                    {b.emoji}
                  </div>
                  <div>
                    <p className="font-bold text-[#1a1a1a]">{b.title}</p>
                    <p className="text-sm text-gray-600">{b.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200 flex items-center gap-2 text-sm text-gray-500">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              We never share your email. Unsubscribe from alerts any time.
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="order-1 lg:order-2 lg:sticky lg:top-24"
          >
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8">
              <h2 className="text-2xl font-black text-[#1a1a1a] mb-1">Create your account</h2>
              <p className="text-sm text-gray-500 mb-6">Already have an account? Click <strong className="text-[#0FA49C]">Sign In</strong> at the top-right.</p>

              <Button
                type="button"
                onClick={handleGoogleLogin}
                variant="outline"
                className="w-full border-gray-300 hover:bg-gray-50 py-6 rounded-xl font-semibold mb-4"
                data-testid="signup-google-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400 uppercase tracking-wider">or</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-gray-700 mb-1.5 block">Your name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Sarah Jones"
                      className="pl-10 py-6"
                      data-testid="signup-name-input"
                      autoComplete="name"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-700 mb-1.5 block">Email address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-10 py-6"
                      data-testid="signup-email-input"
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-700 mb-1.5 block">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      className="pl-10 pr-16 py-6"
                      data-testid="signup-password-input"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-[#0FA49C] hover:underline"
                      data-testid="signup-toggle-password"
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {passwordStrength && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${passwordStrength.bar}`} />
                      </div>
                      <span className={`text-xs font-semibold ${passwordStrength.color}`}>{passwordStrength.label}</span>
                    </div>
                  )}
                </div>

                {error && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="signup-error">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#0FA49C] hover:bg-[#0d8f88] text-white py-6 rounded-xl font-bold text-base disabled:opacity-60"
                  data-testid="signup-submit-btn"
                >
                  {submitting ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Creating account…</>
                  ) : (
                    <>Create Account <ArrowRight className="w-5 h-5 ml-2" /></>
                  )}
                </Button>

                <p className="text-[11px] text-gray-400 text-center mt-3">
                  By creating an account you agree to receive occasional emails about your orders and price alerts. You can unsubscribe any time.
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;
